import { useSelector, useDispatch } from 'react-redux';
import { enqueueSnackbar } from 'notistack';
import { Autocomplete, Box, Button,  DialogContentText, DialogTitle, Dialog, Grid, TextField, Divider, Stack, IconButton} from '@mui/material';
import { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import Iconify from '../../../reusable-components/iconify';
import { UOM_TYPES } from '../../../utils/dropdowns';
import {fNoNegativeOrLetters} from '../../../utils/formatText';
import { AwaitButton } from '../../../reusable-components/await-button';
import {  getCategoryData, getPricingTypeOptions, getRentalItems, getRentalVendors} from '../../../redux/slices/rentals';
import { postEditItem } from '../../../api/rentals';
import CloseIconButton from '../../../reusable-components/basic-buttons/CloseIconButton';

EditRentalItem.propTypes = {
  isEditing: PropTypes.bool, 
  setIsEditing: PropTypes.func,
  values: PropTypes.object,
  setValues: PropTypes.func}
  
export default function EditRentalItem({isEditing, setIsEditing, values, setValues}) {
  const [id, setId] = useState(null);
  const [distinctCategories, setDistinctCategories] = useState([]);
  const dispatch = useDispatch();
  const {data: { categoryData, pricingTypes }} = useSelector((state) => state.rentals);

  const uomTypes = useMemo(() => 
      UOM_TYPES.map((type) => type.label)
    , []);

  useEffect(() => {
    dispatch(getPricingTypeOptions());
    dispatch(getRentalVendors());
    dispatch(getCategoryData());
    setDistinctCategories(
      Array.from(
      new Map(categoryData.map(item => [item.categoryId, item])).values()
      )
    );
    dispatch(getRentalItems());
    const path = window.location.href;
    const parts = path.split('/'); 
    setId(parts.pop()); 
  }, []);

  const deletePricingType = (index) => {
    if (values?.pricingTypes?.length === 1) {
      setValues({
        ...values,
        pricingTypes: [],
      });
    } else{
      setValues((prevData) => ({
        ...prevData,
        pricingTypes: prevData.pricingTypes?.filter((_,i) => i !== index),
      }));
    }
  }
  const updatePricingType = (index, field, value) => {
    if (index === 0 && values?.pricingTypes?.length === 0) {
      setValues((prevData) => ({
        ...prevData,
        pricingTypes: [{ [field]: value }],
      }));
    }
    else{
    setValues((prevData) => ({
      ...prevData,
      pricingTypes: prevData.pricingTypes?.map((pricingType, i) =>
        i === index ? { ...pricingType, [field]: value } : pricingType
      ),
    }))}
};
  
  const clearFormValues = () => {
    setValues({
      description: '',
      category: null,
      subcategory: null,
      notes: '',
      uomType: '',
      uomAmount: '',
      capAmount: '',
      daysToCap: '',
      vendorId: null,
      image: '',
      pricingType: {id: null, name: ''},
      price: '',
      weightLimit: '',
      warranty: '',
      vendorItemLink: '',
      pricingTypes: [],
    });
  };

  const  editRentalItem = async () => {
    const pricingTypeIds = values?.pricingTypes?.map(pt => pt.pricingType);
    const hasDuplicatePricingType = pricingTypeIds.some((id, idx) => id && pricingTypeIds.indexOf(id) !== idx || id === values?.mainPricingType);
    if (hasDuplicatePricingType) {
      enqueueSnackbar('Duplicate pricing types.', { variant: 'error' });
      return;
    }
   const response = await postEditItem(values);
      if (response.data.success) {
        dispatch(getRentalItems());
        enqueueSnackbar('Rental Item Edited', { variant: 'success' });
        setValues({ description: '', category: null, subcategory: null, notes: '' });
      } else {
        enqueueSnackbar('Error while editing item', { variant: 'error' });
      }
      setIsEditing(false);
      clearFormValues();
  }
 return (
     <Dialog open={isEditing}
      onClose={(event, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
        setIsEditing(false);
      }}
      maxWidth="sm"
      sx = {{padding: '12px', margin: "5px"}}
      fullWidth>
        <Grid container direction="row" sx = {{padding: "20px",justifyContent: 'center', alignItems:'center' }} >
          <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <DialogTitle sx = {{textAlign: 'center', position: 'relative', width: '100%' }} >
              Edit Rental Item
              <CloseIconButton onClick={() => setIsEditing(false)}/>
            </DialogTitle>
          </Grid>
          <Grid container sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Grid item xs={12} >
              <DialogContentText sx={{ fontWeight: 'bold' }}>Description </DialogContentText>{' '}
                <TextField
                  size="small"
                  label=""
                  fullWidth
                  multiline
                  value={values?.description || ''}
                  variant="outlined"
                  onChange={(e) => {
                    setValues({...values, description: e.target.value });
                  }}
                  autoComplete="off"
                />
              </Grid>
          </Grid>
          <Grid container xs={12} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Grid item xs={5.75} > 
              <DialogContentText sx={{ fontWeight: 'bold', mt: 2}}> Category *</DialogContentText>
              <Autocomplete
                options={distinctCategories}
                renderInput={(params) => <TextField {...params} size="small" margin="dense" autoComplete="off"/>}
                getOptionLabel={(option) => option?.categoryName || ''}
                value={values.category}
                onChange={(e, value) => {
                setValues({ ...values, category: value, subcategory: null });
                }}
              />
            </Grid>
            <Grid item xs={5.75}>
              <DialogContentText sx={{ fontWeight: 'bold', mt: 2 }}> Subcategory *</DialogContentText>
              <Autocomplete
                options={ categoryData
                  .filter((item) => item.categoryId === values.category?.categoryId && !!item.subcategoryId)
                  .map((item) => ({
                    subcategoryId: item.subcategoryId,
                    subcategoryName: item.subcategoryName,
                  }))}
                renderInput={(params) => <TextField {...params} size="small" margin="dense" autoComplete="off"/>}
                getOptionLabel={(option) => option?.subcategoryName || ''}
                value={values.subcategory}
                onChange={(e, value) => {
                  setValues({ ...values, subcategory: value });
                }}
                noOptionsText="No subcategories found"
              />
            </Grid>
          </Grid>
          <>
          <Grid container xs={12} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Grid item xs={12} >
              <DialogContentText sx={{ display: 'flex' }}>
                <Box sx={{ fontWeight: 'bold', mt:1 }}>Notes</Box>
              </DialogContentText>
          </Grid>
          <Grid item xs = {12}>
            <TextField
              size="small"
              label=""
              value={values?.notes || ''}
              onChange={(e) => {
                setValues({...values, notes: e.target.value });
              }}
              fullWidth
              multiline
              variant="outlined"
              autoComplete="off"
            />
          </Grid>
          </Grid>
          <Stack direction="row" justifyContent="center" alignItems="center" spacing={2} sx={{ mt: 2}}>
            <Box>
              <Box>
                <Divider sx={{ width: '475px', borderColor: 'info.main', borderStyle: 'dashed'}} />
              </Box>
            </Box>
          </Stack>
           <Grid container sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Grid item xs={5.75} >
              <DialogContentText sx={{ fontWeight: 'bold', marginBottom:"8px" }}>UOM Amount</DialogContentText>{' '}
                <TextField
                  size="small"
                  label=""
                  type = "number"
                  fullWidth
                  multiline
                  value={values?.uomAmount || ''}
                  variant="outlined"
                  onChange={(e) => {
                    setValues({...values, uomAmount: e.target.value });
                  }}
                  onKeyDown={(e) => fNoNegativeOrLetters(e)}
                  autoComplete="off"
                />
             </Grid>
             <Grid item xs={5.75}>
              <DialogContentText sx={{ fontWeight: 'bold', mt:1}}> UOM Type *</DialogContentText>
              <Autocomplete
                options={ uomTypes}
                renderInput={(params) => <TextField {...params} size="small" margin="dense" autoComplete="off"/>}
                value={values.uomType}
                onChange={(e, value) => {
                  setValues({ ...values, uomType: value });
                }}
                noOptionsText="No subcategories found"
              />
            </Grid>
          </Grid>
        </>
         <>
         <Grid container sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Grid item xs={5.75}>
              <DialogContentText style={{ fontWeight: 'bold' }}>
                Main Pricing Type *
              </DialogContentText>
              <Autocomplete
                options={pricingTypes}
                getOptionLabel={(option) => option.name || ''}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    margin="dense"
                    size="small"
                    InputProps={{
                      ...params.InputProps,
                    }}
                    autoComplete="off"
                  />
                )}
                value={pricingTypes?.find(f => f.id === values?.pricingTypeId || null)}
                onChange={(e, value) => { setValues({...values, pricingTypeId: value?.id, pricingTypeName: value?.name })}}
              />
            </Grid>
            <Grid item xs={5.75}>
              <DialogContentText style={{ fontWeight: 'bold' }}>Main Price *</DialogContentText>
              <TextField
                fullWidth
                margin="dense"
                type = "number"
                size="small"
                value={values?.price}
                onChange={(e) => { setValues({...values, price: e.target.value})}}
                onKeyDown={(e) => fNoNegativeOrLetters(e)}
                autoComplete="off"
              />
            </Grid>
          </Grid>
          {values?.pricingTypes?.map((pricingType, index) => (
            <Grid
              container
              key={pricingType.pricingType || index}
              sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
            >
              <Grid item xs={5.5}>
                <DialogContentText style={{ fontWeight: 'bold' }}>
                  Pricing Type 
                </DialogContentText>
                <Autocomplete
                  options={pricingTypes}
                  getOptionLabel={(option) => option.name || ''}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      margin="dense"
                      size="small"
                    />
                  )}
                  value={pricingTypes?.find(f => f?.id === pricingType?.pricingType)}
                  onChange={(e, value) => updatePricingType(index, 'pricingType', value.id)}
                  autoComplete="off"
                />
              </Grid>
              <Grid item xs={5.5}>
                <DialogContentText style={{ fontWeight: 'bold' }}>Price </DialogContentText>
                <TextField
                  fullWidth
                  margin="dense"
                  type = "number"
                  size="small"
                  value={pricingType.price}
                  onChange={(e) => updatePricingType(index, 'price', e.target.value)}
                  onKeyDown={(e) => fNoNegativeOrLetters(e)}
                  autoComplete="off"
                />
              </Grid>
              <Grid item xs = {.5} style = {{marginTop: '26px'}}>
                <IconButton
                  onClick={() => { deletePricingType(index) }}
                >
                  <Iconify icon="material-symbols:delete-outline" width={20} height={20} color="error" />
                </IconButton>
              </Grid>
            </Grid>
           ))
          }
          
          <Grid container direction="row" justifyContent="space-between" alignItems="center">
            <Button
              variant="text"
              color="primary"
              sx={{
                textDecoration: 'underline',
              }}
              onClick={() => setValues({...values, pricingTypes: [...values?.pricingTypes, { pricingType: null, price: null }]})}
            >
              Add additional pricing type
            </Button>
          </Grid>
          <Grid container sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Grid item xs={5.75}>
              <DialogContentText style={{ fontWeight: 'bold', mt:2, }}>Cap Rate ($)</DialogContentText>
              <TextField
                size="small"
                fullWidth
                type = "number"
                value={values.capAmount}
                onChange={(e) => setValues({ ...values, capAmount: e.target.value })}
                onKeyDown={(e) => fNoNegativeOrLetters(e)}
                autoComplete="off"
              />
            </Grid>
            <Grid style={{ flex: 1 }} xs={5.75}>
              <DialogContentText style={{ fontWeight: 'bold', mt: 2 }}>Days to Cap</DialogContentText>
              <TextField
                size="small"
                type='number'
                fullWidth
                value={values.daysToCap}
                onChange={(e) => setValues({ ...values, daysToCap: e.target.value })}
                onKeyDown={(e) => fNoNegativeOrLetters(e)}
                autoComplete="off"
              />
            </Grid>
          </Grid>
          <Grid container sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Grid item xs={5.75}>
              <DialogContentText style={{ fontWeight: 'bold', mt:2, }}>Weight Limit</DialogContentText>
              <TextField
                size="small"
                fullWidth
                value={values.weightLimit}
                onChange={(e) => setValues({ ...values, weightLimit: e.target.value })}
                autoComplete="off"
              />
            </Grid>
            <Grid style={{ flex: 1 }} xs={5.75}>
              <DialogContentText style={{ fontWeight: 'bold', mt: 2 }}>Warranty</DialogContentText>
              <TextField
                size="small"
                fullWidth
                value={values.warranty}
                onChange={(e) => setValues({ ...values, warranty: e.target.value })}
                autoComplete="off"
              />
            </Grid>
          </Grid>
          <Stack direction="row" justifyContent="center" alignItems="center" spacing={2}sx={{ mt: 2}}>
            <Box>
              <Box>
                <Divider sx={{ width: '475px', borderColor: 'info.main', borderStyle: 'dashed' }} />
              </Box>
            </Box>
          </Stack>
          <Grid container sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Grid item xs={12}>
              <DialogContentText style={{ fontWeight: 'bold',marginTop:'4px'  }}>Vendor Item Link</DialogContentText>
              <TextField
                size = "small"
                fullWidth
                value={values.vendorItemLink}
                onChange={(e) => setValues({ ...values, vendorItemLink: e.target.value })}
                autoComplete="off"
              />
            </Grid>
          </Grid>
          <Grid container sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Grid item xs={12}>
              <DialogContentText style={{ fontWeight: 'bold',marginTop:'4px' }}>Image URL</DialogContentText>
              <TextField
                size='small'
                fullWidth
                value={values.image}
                onChange={(e) => setValues({ ...values, image: e.target.value })}
                autoComplete="off"
              />
            </Grid>
          </Grid>
        </>
        <Grid container direction="row" justifyContent="flex-end" alignItems="center">
          {' '}
          <Button
            variant="outlined"
            color="error"
            size="medium"
            sx={{ width: 150, mr: 2, mt: 2 }}
            onClick={() => {clearFormValues(); setIsEditing(false);}}
          >
            Cancel
          </Button>
          <AwaitButton
            variant="contained"
            color="primary"
            size="medium"
            sx={{ width: 150, mt: 2 }}
            disabled={
              !values?.description ||
              !values?.category ||
              !values?.subcategory
            }
            onClick={() => {editRentalItem(id, values)}}
          >
            Save
          </AwaitButton>{' '}
        </Grid>
      </Grid>
    </Dialog>
  );
}
